import {
  ChartDataContext,
  ChartDateRange,
  ChartLegendPosition,
  ChartTypeEnum,
} from '@finxone-platform/shared/enums';
import { UUID } from '@finxone-platform/shared/base-types';
import {
  APP_ZONES,
  ActionButtonOutcomesType,
  AppBuilderPreviewState,
  BorderRadiusClass,
  BorderRadiusKeys,
  BorderStyles,
  ButtonSizeOptions,
  ButtonTypes,
  Constants,
  DocumentActionCategoryType,
  DocumentActionType,
  DummyRole,
  GridPosition,
  InputCssConfig,
  InputFieldSettings,
  NavigationOptionEnum,
  Orientation,
  Page,
  ProductFilterAccountType,
  ProductFilterCustomerType,
  ProductFilterJourneyType,
  WORKFLOW_POPUPS,
  WidgetNames,
  WidgetTitles,
  WorkflowWidgetConfig,
  WorkflowWidgetConfiguration,
  cryptoRandomUUID,
} from '@finxone-platform/shared/sys-config-types';
import { alphaNumericWithSpecialCharsRegEx } from '../../regex.util';
import { inputSettings } from './payments/payments.type';

export type CustomWorkflowWidgetConfig = {
  // optional fields to be used in function arguments
  widgetTypeConfig?: 'new';
  uniqueId?: string | number;
  name?: string;
  widgetConfiguration?: WorkflowWidgetConfiguration;
  gridPosition?: GridPosition;
  widgetIconPath?: string;
  alternativeAddWidgets?: WorkflowWidgetConfig[];
  zoneHook?: string;
  roleHook?: string;
  popupType?: WORKFLOW_POPUPS | null;
};

export const lineWidgetWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: Math.floor(Math.random() * (1000000000 + 1)),
  name: WidgetNames.LINE_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.LINE_WIDGET,
    widgetConfigDisplayName: 'Spacer : Line',
    isRemovable: true,
    attributes: {
      widgetProperties: {
        textContent: '',
        show: true,
        color: 'white',
        paddingTop: 20,
        paddingBottom: 20,
        lineThickness: 1,
        lineWidth: 90,
        contentAlignment: 'center',
      },
      css_config: {},
      elementsConfig: [
        {
          field_name: WidgetNames.LINE_WIDGET,
          label: 'line',
          type: 'text',
          section: WidgetNames.LINE_WIDGET,
          show: true,
          options: [
            {
              field_type: 'line',
              field_name: WidgetNames.LINE_WIDGET,
              label: 'line',
              type: 'text',
              show: true,
            },
          ],
        },
      ],
      label: '',
      sub_heading: '',
      show: true,
      field_attributes: [
        {
          name: WidgetNames.LINE_WIDGET,
          label: 'line',
          type: 'text',
          options: [],
          decision_config: {
            required: true,
          },
          additional_config: {
            show: true,
            color: 'white',
            paddingTop: 20,
            paddingBottom: 20,
            lineThickness: 1,
            lineWidth: 90,
            contentAlignment: 'center',
          },
        },
      ],
    },
  },
  gridPosition: {
    x: 0,
    y: 0,
    minItemCols: 1,
    minItemRows: 1,
    rows: 2,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'line-widget-icon',
} as WorkflowWidgetConfig;

export const currencyInputFieldWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.CURRENCY_INPUT_FIELD_WIDGET,
  widgetConfiguration: {
    uniqueId: cryptoRandomUUID(),
    widgetDisplayName: WidgetTitles.CURRENCY_INPUT_FIELD_WIDGET,
    widgetConfigDisplayName: 'Input Field: Amount',
    isRemovable: true,
    attributes: {
      inputMode: 'decimal',
      elementsConfig: [],
      label: '',
      sub_heading: '',
      show: true,
      css_config: {
        ...Constants.defaultBaseCssConfig,
        inputPlaceholderColor: 'rgb(0, 0, 0)',
        shadowBlur: 0,
        shadowSpread: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        fontSize: 40,
        textAlignment: 'center',
      },
      widgetProperties: {
        textContent: '',
        inputPlaceholder: '0.00',
        inputFieldKey: 'amount',
      },
    },
  },
  gridPosition: {
    x: 0,
    y: 8,
    minItemCols: 1,
    minItemRows: 1,
    rows: 2,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: '',
} as WorkflowWidgetConfig;

export const paymentRefConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.PAYMENT_REF_WIDGET,
  widgetConfiguration: {
    uniqueId: cryptoRandomUUID(),
    widgetDisplayName: WidgetTitles.PAYMENT_REF_WIDGET,
    widgetConfigDisplayName: 'Payment Reference',
    isRemovable: false,
    attributes: {
      elementsConfig: [],
      label: '',
      sub_heading: '',
      show: true,
      css_config: {
        ...Constants.defaultBaseCssConfig,
        fontSize: 14,
        textAlignment: 'left',
        borderWidth: 1,
        borderColor: 'rgba(197, 197, 197, 1)',
        borderStyle: 'solid',
        inputPlaceholderColor: 'rgba(94, 101, 102, 1)',
        shadowBlur: 0,
        shadowSpread: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        borderRadiusTopLeft: 2,
        borderRadiusBottomLeft: 2,
        borderRadiusTopRight: 2,
        borderRadiusBottomRight: 2,
      },
      widgetProperties: {
        textContent: '',
        displayName: 'Payment reference',
        inputPlaceholder: 'Payment Reference',
        inputFieldKey: 'paymentReference',
        pattern: alphaNumericWithSpecialCharsRegEx,
        inputLabel: 'Payment Reference',
      },
    },
  },
  gridPosition: {
    x: 0,
    y: 11,
    minItemCols: 1,
    minItemRows: 1,
    rows: 3,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: '',
} as WorkflowWidgetConfig;

export const paymentReasonWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.PAYMENT_REASON_WIDGET,
  widgetConfiguration: {
    uniqueId: cryptoRandomUUID(),
    widgetDisplayName: WidgetTitles.PAYMENT_REASON,
    widgetConfigDisplayName: 'Payment Reason',
    isRemovable: true,
    attributes: {
      elementsConfig: [],
      label: '',
      sub_heading: '',
      show: true,
      css_config: {
        ...Constants.defaultBaseCssConfig,
        fontSize: 14,
        textAlignment: 'left',
        borderWidth: 1,
        borderColor: 'var(--color-border-color)',
        borderStyle: 'solid',
        inputPlaceholderColor: 'rgba(94, 101, 102, 1)',
        shadowBlur: 0,
        shadowSpread: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        borderRadiusTopLeft: 2,
        borderRadiusBottomLeft: 2,
        borderRadiusTopRight: 2,
        borderRadiusBottomRight: 2,
      },
      widgetProperties: {
        textContent: '',
        inputPlaceholder: 'Payment Reason',
        inputFieldKey: 'transferReasonId',
        inputLabel: 'Payment Reason',
      },
    },
  },
  gridPosition: {
    x: 0,
    y: 15,
    minItemCols: 1,
    minItemRows: 1,
    rows: 3,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'input-field-widget',
} as WorkflowWidgetConfig;

export function getCTAButtonsWidgetConfig(
  type: WidgetNames,
  title: WidgetTitles | string,
): WorkflowWidgetConfig {
  return {
    widgetTypeConfig: 'new',
    uniqueId: cryptoRandomUUID(),
    name: type,
    widgetConfiguration: {
      uniqueId: cryptoRandomUUID(),
      widgetDisplayName: title,
      widgetConfigDisplayName: 'CTA Button',
      isRemovable: false,
      attributes: {
        elementsConfig: [],
        label: '',
        sub_heading: '',
        show: true,
        css_config: {
          ...Constants.defaultBaseCssConfig,
          textAlignment: 'center',
          backGroundColor: 'var(--color-primary)',
          color: 'var(--color-background)',
          fontSize: 16,
          shadowBlur: 0,
          shadowSpread: 0,
          shadowOffsetX: 0,
          shadowOffsetY: 0,
          borderRadiusTopLeft: 5,
          borderRadiusBottomLeft: 5,
          borderRadiusTopRight: 5,
          borderRadiusBottomRight: 5,
        },
        widgetProperties: {
          textContent: 'Next',
          zoneToNavigate: APP_ZONES.PAYMENT,
          urlToNavigate: 'payment-summary',
          delayButtonActivation: false,
        },
      },
    },
    gridPosition: {
      x: 0,
      y: 0,
      minItemCols: 1,
      minItemRows: 1,
      rows: 3,
      cols: 15,
      resizeEnabled: true,
      dragEnabled: true,
    },
    widgetIconPath: 'action-button',
  };
}

export const textWidgetWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.TEXT_WIDGET,
  widgetConfiguration: {
    uniqueId: cryptoRandomUUID(),
    widgetDisplayName: WidgetTitles.TEXT_WIDGET,
    widgetConfigDisplayName: 'Text / HTML',
    isRemovable: true,
    attributes: {
      elementsConfig: [],
      label: '',
      sub_heading: '',
      show: true,
      css_config: {
        ...Constants.defaultBaseCssConfig,
        textAlignment: 'center',
        verticalAlignment: 'center',
        fontSize: 16,
      },
      widgetProperties: {
        textContent: 'Text / HTML',
        textStyleClass: 'body1',
      },
    },
  },
  gridPosition: {
    x: 0,
    y: 0,
    minItemCols: 1,
    minItemRows: 1,
    rows: 2,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'text-widget',
} as WorkflowWidgetConfig;

export function createTextWidgetConfig(
  textContent: string,
  textAlignment: string,
  color: string,
  fontSize: number,
  displayName?: string,
  rows = 2,
  cols = 15,
): WorkflowWidgetConfig {
  return {
    widgetTypeConfig: 'new',
    uniqueId: cryptoRandomUUID(),
    name: WidgetNames.TEXT_WIDGET,
    widgetConfiguration: {
      uniqueId: cryptoRandomUUID(),
      widgetDisplayName: WidgetTitles.TEXT_WIDGET,
      widgetConfigDisplayName: displayName ?? 'Text / HTML',
      isRemovable: true,
      attributes: {
        elementsConfig: [],
        label: '',
        sub_heading: '',
        show: true,
        css_config: {
          ...Constants.defaultBaseCssConfig,
          textAlignment: textAlignment,
          verticalAlignment: 'center',
          color: color,
          fontSize: fontSize,
        },
        widgetProperties: {
          textContent: textContent,
        },
      },
    },
    gridPosition: {
      x: 0,
      y: 0,
      minItemCols: 1,
      minItemRows: 1,
      rows: rows,
      cols: cols,
      resizeEnabled: true,
      dragEnabled: true,
    },
    widgetIconPath: 'text-widget',
  } as WorkflowWidgetConfig;
}

export const iFrameWidgetWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: Math.floor(Math.random() * (1000000000 + 1)),
  name: WidgetNames.IFRAME_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.IFRAME_WIDGET,
    widgetConfigDisplayName: 'iFrame',
    isRemovable: true,
    attributes: {
      widgetProperties: {
        textContent: '',
        iframeUrl: '',
        verticalHeight: '30',
        showIFrameWidget: true,
      },
      elementsConfig: [
        {
          field_name: WidgetNames.IFRAME_WIDGET,
          label: 'iframe',
          type: 'text',
          section: WidgetNames.IFRAME_WIDGET,
          show: true,
          options: [
            {
              field_type: 'iframe',
              field_name: WidgetNames.IFRAME_WIDGET,
              label: 'iframe',
              type: 'text',
              show: true,
            },
          ],
        },
      ],
      label: '',
      sub_heading: '',
      show: true,
      field_attributes: [
        {
          name: WidgetNames.IFRAME_WIDGET,
          label: 'iframe',
          type: 'text',
          options: [],
          decision_config: {
            required: true,
          },
          additional_config: {},
        },
      ],
      css_config: {
        ...Constants.defaultBaseCssConfig,
      },
    },
  },
  gridPosition: {
    x: 0,
    y: 0,
    minItemCols: 5,
    minItemRows: 2,
    rows: 6,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'iframe',
} as WorkflowWidgetConfig;

export const customWidgetWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: Math.floor(Math.random() * (1000000000 + 1)),
  name: WidgetNames.CUSTOM_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.CUSTOM_WIDGET,
    widgetConfigDisplayName: WidgetTitles.CUSTOM_WIDGET,
    isRemovable: true,
    attributes: {
      elementsConfig: [],
      // Shape must match CustomWidgetInstanceProperties (web-frontend custom-widget-host.utils.ts)
      widgetProperties: {
        textContent: '',
        customWidget: {
          widgetId: '',
          version: '',
          bundleUrl: '',
          settings: {},
          grants: { data: [], api: [], forms: true, nav: true },
          defaultHeight: 320,
        },
      },
      label: '',
      sub_heading: '',
      show: true,
      css_config: {
        ...Constants.defaultBaseCssConfig,
      },
    },
  },
  gridPosition: {
    x: 0,
    y: 0,
    minItemCols: 5,
    minItemRows: 2,
    rows: 8,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'iframe',
} as WorkflowWidgetConfig;

export const imageWidgetWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: Math.floor(Math.random() * (1000000000 + 1)),
  name: WidgetNames.IMAGE_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.IMAGE_WIDGET,
    widgetConfigDisplayName: 'Single Image',
    isRemovable: true,
    attributes: {
      widgetProperties: { textContent: '' },
      elementsConfig: [
        {
          field_name: 'image_widget',
          label: 'Image.png',
          type: 'file',
          section: 'image_widget',
          show: true,
          options: [],
        },
      ],
      outerCss: 'height: 15vh;',
      css_config: Constants.defaultBaseCssConfig,
    },
  },
  gridPosition: {
    x: 0,
    y: 0,
    minItemCols: 5,
    minItemRows: 2,
    rows: 4,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'image-widget',
  popupType: WORKFLOW_POPUPS.IMAGE_WIDGET,
} as WorkflowWidgetConfig;

export function createWorkflowWidgetConfig(
  leftIcon: string,
  itemTemplate: string,
  pageName: Page,
  widgetTitle: WidgetTitles | string,
  rightIcon?: string,
  horizentalAlignment?: string,
): CustomWorkflowWidgetConfig {
  return {
    widgetConfiguration: {
      widgetDisplayName: widgetTitle,
      widgetConfigDisplayName: widgetTitle,
      attributes: {
        elementsConfig: [],
        name: pageName,
        type: 'links',
        slug: pageName,
        label: widgetTitle,
        zone: '',
        role: '',
        css_config: {
          ...Constants.defaultBaseCssConfig,
          textAlignment: horizentalAlignment ?? 'center',
          verticalAlignment: 'center',
          textFont: 'Metropolis',
          fontSize: 20,
          shadowBlur: 0,
          shadowSpread: 0,
          shadowOffsetX: 0,
          shadowOffsetY: 0,
          borderWidth: '1',
          borderColor: '#EBEBEB',
          backGroundColor: '#fff',
          color: '#384F80',
        },
        widgetProperties: {
          textContent: '',
        },
        pageUrl: '',
        iconConfig: {
          leftIcon,
          rightIcon: rightIcon ?? '',
        },
        itemConfig: {
          itemTemplate,
        },
      },
      isRemovable: true,
    } as WorkflowWidgetConfiguration,
  };
}

export const copyShareContentWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.COPY_SHARE_CONTENT,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.COPY_SHARE_CONTENT,
    widgetConfigDisplayName: 'Copy/Share Content',
    attributes: {
      elementsConfig: [],
      showShareSheet: false,
      textContent: {
        textContentToDisplay: `<span class="d-flex justify-center align-items-center gap-1">Copy Details {{{ svgIcon 'copy-icon' }}}</span>`,
        textContentToCopy: '',
      },
      css_config: {
        ...Constants.defaultBaseCssConfig,
      },
      widgetProperties: {
        textContent: '',
        globalStyling: true,
        globalStylingClass: `${ButtonTypes.PRIMARY} ${ButtonSizeOptions.FULL_WIDTH}`,
      },
    },
    isRemovable: true,
  },
  gridPosition: {
    x: 0,
    y: 1,
    minItemCols: 1,
    minItemRows: 1,
    rows: 2,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'copy-share',
} as WorkflowWidgetConfig;

export function addToGooglePayWidgetConfig(): WorkflowWidgetConfig {
  const WidgetConfig: WorkflowWidgetConfig = {
    widgetTypeConfig: 'new',
    uniqueId: cryptoRandomUUID(),
    name: WidgetNames.ADD_TO_GOOGLE_PAY_BUTTON,
    widgetConfiguration: {
      widgetDisplayName: WidgetTitles.ADD_TO_GOOGLE_PAY_BUTTON,
      widgetConfigDisplayName: 'Add to Google Pay',
      attributes: {
        elementsConfig: [],
        textContent: {
          textContentToDisplay: '',
          textContentToCopy: '',
        },
        css_config: {
          marginUnit: 'px',
          marginTop: 0,
          marginLeft: 0,
          marginRight: 0,
          marginBottom: 0,
          borderRadius: 0,
          width: 335,
          height: 44,
          textWidth: 92,
          textHeight: 18,
        },
        widgetProperties: {
          textContent: '',
          buttonColorType: '#000000',
        },
      },
      isRemovable: true,
    },
    gridPosition: {
      x: 0,
      y: 1,
      minItemCols: 1,
      minItemRows: 1,
      rows: 2,
      cols: 15,
      resizeEnabled: true,
      dragEnabled: true,
    },
    widgetIconPath: 'wallet',
  };
  return WidgetConfig;
}

export function addToAppleWalletWidgetConfig(): WorkflowWidgetConfig {
  return {
    widgetTypeConfig: 'new',
    uniqueId: cryptoRandomUUID(),
    name: WidgetNames.ADD_TO_APPLE_WALLET_BUTTON,
    widgetConfiguration: {
      widgetDisplayName: WidgetTitles.ADD_TO_APPLE_WALLET_BUTTON,
      widgetConfigDisplayName: 'Add to Apple Wallet',
      attributes: {
        elementsConfig: [],
        textContent: {
          textContentToDisplay: '',
          textContentToCopy: '',
        },
        css_config: {
          marginUnit: 'px',
          marginTop: 0,
          marginLeft: 0,
          marginRight: 0,
          marginBottom: 0,
          paddingUnit: 'px',
          paddingTop: 0,
          paddingLeft: 0,
          paddingRight: 0,
          paddingBottom: 0,
          width: 193,
        },
        widgetProperties: {
          textContent: '',
        },
      },
      isRemovable: true,
    },
    gridPosition: {
      x: 0,
      y: 1,
      minItemCols: 1,
      minItemRows: 1,
      rows: 3,
      cols: 15,
      resizeEnabled: true,
      dragEnabled: true,
    },
    widgetIconPath: 'wallet',
  };
}

export const formButtonWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: new UUID().toString(),
  name: WidgetNames.FORM_BUTTON_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.FORM_BUTTON_WIDGET,
    widgetConfigDisplayName: WidgetTitles.FORM_BUTTON_WIDGET,
    attributes: {
      elementsConfig: [],
      css_config: {
        ...Constants.defaultBaseCssConfig,
        color: 'var(--color-background)',
        backGroundColor: 'var(--color-primary)',
        textAlignment: 'center',
        justifyContent: 'center',
        verticalAlignment: 'center',
        borderColor: 'var(--color-primary)',
        borderStyle: 'solid',
        borderWidth: 1,
        borderRadiusTopLeft: 6,
        borderRadiusBottomLeft: 6,
        borderRadiusTopRight: 6,
        borderRadiusBottomRight: 6,
        textFont: 'var(--font-family)',
        fontSize: 16,
        paddingTop: 10,
        paddingLeft: 10,
        paddingRight: 10,
        paddingBottom: 10,
        paddingUnit: 'px',
      },
      widgetProperties: {
        textContent: 'Proceed',
        righticon: '',
        lefticon: '',
        role: '',
        zone: '',
        pageUrl: '',
        formButtonSubmissionType: 'navigateOnly', // default value
      },
    },
    isRemovable: true,
  },
  widgetIconPath: 'action-button',
} as WorkflowWidgetConfig;

export const bankListingWidgetConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: new UUID().toString(),
  name: WidgetNames.BANK_LISTING_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.BANK_LISTING_WIDGET,
    widgetConfigDisplayName: WidgetTitles.BANK_LISTING_WIDGET,
    attributes: {
      elementsConfig: [],
      css_config: {
        ...Constants.defaultBaseCssConfig,
        color: 'var(--color-on-surface)',
        backGroundColor: 'var(--color-surface)',
        textAlignment: 'center',
        justifyContent: 'center',
        verticalAlignment: 'center',
        borderColor: 'transparent',
        borderStyle: 'solid',
        borderWidth: 1,
        borderRadiusTopLeft: 6,
        borderRadiusBottomLeft: 6,
        borderRadiusTopRight: 6,
        borderRadiusBottomRight: 6,
        textFont: 'var(--font-family)',
        fontSize: 16,
        paddingTop: 10,
        paddingLeft: 10,
        paddingRight: 10,
        paddingBottom: 10,
        paddingUnit: 'px',
      },
      widgetProperties: {
        textContent: `
          <div class="d-flex align-items-center gap-3">
            <img height="50px" width="50px" src="{{ bankLogo }}" alt="bankLogo">
            <div class="body1 text-on-surface">{{ bankName }}</div>
          </div>
        `,
        inputFieldSettings: {
          ...inputSettings,
          css_config: {
            inputWidth: 100,
            iconSize: 24,
            iconSizeUnit: 'px',
            inputPosition: 'start',
          } as InputCssConfig,
          label: 'Search',
          type: 'search',
          isIconAllowed: true,
          isAllowed: false,
        } as InputFieldSettings,
        itemsPerRow: 1,
        loadingTextContent: 'Fetching supported banks...',
        appBuilderState: AppBuilderPreviewState.DEFAULT,
      },
      noRecordsTemplate: 'No records',
    },
    isRemovable: true,
  },
  widgetIconPath: 'listing',
};

export const pispBankListWidgetConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: new UUID().toString(),
  name: WidgetNames.PISP_BANK_LIST_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.PISP_BANK_LIST_WIDGET,
    widgetConfigDisplayName: WidgetTitles.PISP_BANK_LIST_WIDGET,
    attributes: {
      elementsConfig: [],
      css_config: {
        ...Constants.defaultBaseCssConfig,
        color: 'var(--color-on-surface)',
        backGroundColor: 'var(--color-surface)',
        textAlignment: 'center',
        justifyContent: 'center',
        verticalAlignment: 'center',
        borderColor: 'transparent',
        borderStyle: 'solid',
        borderWidth: 1,
        borderRadiusTopLeft: 6,
        borderRadiusBottomLeft: 6,
        borderRadiusTopRight: 6,
        borderRadiusBottomRight: 6,
        textFont: 'var(--font-family)',
        fontSize: 16,
        paddingTop: 10,
        paddingLeft: 10,
        paddingRight: 10,
        paddingBottom: 10,
        paddingUnit: 'px',
      },
      widgetProperties: {
        textContent: `
          <div class="d-flex align-items-center gap-3">
            <img height="50px" width="50px" src="{{ bankLogo }}" alt="bankLogo">
            <div class="body1 text-on-surface">{{ bankName }}</div>
          </div>
        `,
        inputFieldSettings: {
          ...inputSettings,
          css_config: {
            inputWidth: 100,
            iconSize: 24,
            iconSizeUnit: 'px',
            inputPosition: 'start',
          },
          label: 'Search',
          type: 'search',
          isIconAllowed: true,
          isAllowed: false,
        } as InputFieldSettings,
        itemsPerRow: 1,
        loadingTextContent: 'Fetching supported banks...',
      },
      noRecordsTemplate: 'No records',
    },
    isRemovable: true,
  },
  widgetIconPath: 'listing',
};

export const gocardlessComplianceWidgetConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: new UUID().toString(),
  name: WidgetNames.GOCARDLESS_COMPLIANCE_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.GOCARDLESS_COMPLIANCE_WIDGET,
    widgetConfigDisplayName: WidgetTitles.GOCARDLESS_COMPLIANCE_WIDGET,
    attributes: {
      elementsConfig: [],
      css_config: {
        ...Constants.defaultBaseCssConfig,
        fontSize: 1.6,
        fontUnit: 'rem',
      },
      widgetProperties: {
        textContent: ``,
      },
    },
    isRemovable: true,
  },
  widgetIconPath: 'integration',
};

export const pispCheckBillingRequestLoaderWidgetConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: new UUID().toString(),
  name: WidgetNames.PISP_CHECK_BILLING_REQUEST_LOADER,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.PISP_CHECK_BILLING_REQUEST_LOADER_WIDGET,
    widgetConfigDisplayName: WidgetTitles.PISP_CHECK_BILLING_REQUEST_LOADER_WIDGET,
    attributes: {
      elementsConfig: [],
      css_config: {
        ...Constants.defaultBaseCssConfig,
      },
      widgetProperties: {
        textContent: ``,
      },
    },
    isRemovable: true,
  },
  widgetIconPath: 'integration',
};

export const accountSelectionWidgetConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: new UUID().toString(),
  name: WidgetNames.ACCOUNT_SELECTION_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.ACCOUNT_SELECTION_WIDGET,
    widgetConfigDisplayName: WidgetTitles.ACCOUNT_SELECTION_WIDGET,
    attributes: {
      elementsConfig: [],
      css_config: {
        ...Constants.defaultBaseCssConfig,
        color: 'var(--color-on-surface)',
        backGroundColor: 'var(--color-surface)',
        textAlignment: 'left',
        justifyContent: 'start',
        verticalAlignment: 'flex-start',
        borderColor: 'transparent',
        borderStyle: 'solid',
        borderWidth: 1,
        borderRadiusTopLeft: 6,
        borderRadiusBottomLeft: 6,
        borderRadiusTopRight: 6,
        borderRadiusBottomRight: 6,
        textFont: 'var(--font-family)',
        fontSize: 16,
        paddingTop: 10,
        paddingLeft: 10,
        paddingRight: 10,
        paddingBottom: 10,
        paddingUnit: 'px',
      },
      widgetProperties: {
        textContent: `
          <div>
            <div class="body1 textOnSurface">{{ accountOwnerName }}</div>
            <div class="body1 textOnSurfaceMuted">{{ accountName }}, {{ accountProduct }}, {{ cashAccountType }}</div>
            <div class="body1 textOnSurfaceMuted">{{ accountIban }}</div>
          </div>
        `,
        loadingTextContent: 'Fetching your accounts...',
      },
      noRecordsTemplate: 'No records',
    },
    isRemovable: true,
  },
  widgetIconPath: 'listing',
};

export const displayDocumentWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: new UUID().toString(),
  name: WidgetNames.DISPLAY_DOCUMENT_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.DISPLAY_DOCUMENT_WIDGET,
    widgetConfigDisplayName: WidgetTitles.DISPLAY_DOCUMENT_WIDGET,
    attributes: {
      elementsConfig: [],
      css_config: {
        marginUnit: 'px',
        marginTop: 0,
        marginLeft: 0,
        marginRight: 0,
        marginBottom: 0,
        paddingUnit: 'px',
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: 0,
        borderWidth: 1,
        borderRadiusTopLeft: 6,
        borderRadiusBottomLeft: 6,
        borderRadiusTopRight: 6,
        borderRadiusBottomRight: 6,
      },
      widgetProperties: {
        textContent: '',
        documentActionType: DocumentActionType.TRANSACTIONS,
        fetchingTemplate: '',
        noDocumentTemplate: 'No Document Found',
        showExpandDocument: true,
        outerCss: `height:40vh;`,
      },
    },
    isRemovable: true,
  },
  widgetIconPath: 'document',
} as WorkflowWidgetConfig;

export const paymentCancellationWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: new UUID().toString(),
  name: WidgetNames.PAYMENT_CANCELLATION_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.PAYMENT_CANCELLATION_WIDGET,
    widgetConfigDisplayName: WidgetTitles.PAYMENT_CANCELLATION_WIDGET,
    attributes: {
      elementsConfig: [],
      css_config: {
        marginUnit: 'px',
        marginTop: 0,
        marginLeft: 0,
        marginRight: 0,
        marginBottom: 0,
        paddingUnit: 'px',
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: 0,
        borderWidth: 0,
        borderRadiusTopLeft: 0,
        borderRadiusBottomLeft: 0,
        borderRadiusTopRight: 0,
        borderRadiusBottomRight: 0,
      },
      widgetProperties: {
        textContent: '',
        actionNavigationConfigurations: {
          [ActionButtonOutcomesType.SUCCESS]: {
            selectedZone: APP_ZONES.LANDING,
            selectedRole: DummyRole.EMPTY_ROLE,
            selectedPageIndex: 0,
            selectedPageUrl: 'home',
            selectedPageId: '',
            tabRef: '',
            selectedBrowserBehaviour: '',
            externalUrl: '',
            selectedNavigationOption: NavigationOptionEnum.USE_AS_NAVIGATION,
            pageTransition: 'forward',
          },
          [ActionButtonOutcomesType.FAILURE]: {
            selectedZone: APP_ZONES.LANDING,
            selectedRole: DummyRole.EMPTY_ROLE,
            selectedPageIndex: 0,
            selectedPageUrl: 'home',
            selectedPageId: '',
            tabRef: '',
            selectedBrowserBehaviour: '',
            externalUrl: '',
            selectedNavigationOption: NavigationOptionEnum.USE_AS_NAVIGATION,
            pageTransition: 'forward',
          },
        },
      },
    },
    isRemovable: true,
  },
  widgetIconPath: 'action-button',
} as WorkflowWidgetConfig;

export const cancellablePaymentsInfoWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: new UUID().toString(),
  name: WidgetNames.CANCEL_PAYMENTS_INFO_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.CANCEL_PAYMENTS_INFO_WIDGET,
    widgetConfigDisplayName: WidgetTitles.CANCEL_PAYMENTS_INFO_WIDGET,
    attributes: {
      elementsConfig: [],
      css_config: {
        marginUnit: 'px',
        marginTop: 0,
        marginLeft: 0,
        marginRight: 0,
        marginBottom: 0,
        paddingUnit: 'px',
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: 0,
        borderWidth: 0,
        borderRadiusTopLeft: 0,
        borderRadiusBottomLeft: 0,
        borderRadiusTopRight: 0,
        borderRadiusBottomRight: 0,
      },
      widgetProperties: {
        textContent: '',
        actionNavigationConfigurations: {
          [ActionButtonOutcomesType.SUCCESS]: {
            selectedZone: APP_ZONES.LANDING,
            selectedRole: DummyRole.EMPTY_ROLE,
            selectedPageIndex: 0,
            selectedPageUrl: 'home',
            selectedPageId: '',
            tabRef: '',
            selectedBrowserBehaviour: '',
            externalUrl: '',
            selectedNavigationOption: NavigationOptionEnum.USE_AS_NAVIGATION,
            pageTransition: 'forward',
          },
          [ActionButtonOutcomesType.FAILURE]: {
            selectedZone: APP_ZONES.LANDING,
            selectedRole: DummyRole.EMPTY_ROLE,
            selectedPageIndex: 0,
            selectedPageUrl: 'home',
            selectedPageId: '',
            tabRef: '',
            selectedBrowserBehaviour: '',
            externalUrl: '',
            selectedNavigationOption: NavigationOptionEnum.USE_AS_NAVIGATION,
            pageTransition: 'forward',
          },
        },
      },
    },
    isRemovable: true,
  },
  widgetIconPath: 'generic-widget',
} as WorkflowWidgetConfig;

export const chartConfig = {
  widgetTypeConfig: 'new',
  uniqueId: new UUID().toString(),
  name: WidgetNames.CHART_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.CHART_WIDGET,
    widgetConfigDisplayName: WidgetTitles.CHART_WIDGET,
    subtitle: 'General',
    attributes: {
      elementsConfig: [],
      css_config: {
        ...Constants.defaultBaseCssConfig,
        borderWidthTop: 1,
        borderWidthBottom: 1,
        borderWidthLeft: 1,
        borderWidthRight: 1,
        borderWidthUnit: 'px',
        borderRadiusTopLeft: 6,
        borderRadiusBottomLeft: 6,
        borderRadiusTopRight: 6,
        borderRadiusBottomRight: 6,
        borderStyle: BorderStyles.SOLID,
        borderClass: BorderRadiusKeys.NONE,
      },
      widgetProperties: {
        textContent: '',
        dataContext: ChartDataContext.TOTAL_SPENDING,
        chartLayout: [ChartTypeEnum.BAR, ChartTypeEnum.LINE],
        chartDateRange: ChartDateRange.WEEK,
        textInputHeader: 'Total spending',
        chartRhsAlignment: 'space-between',
        dateRangeDropdown: false,
        chartLayoutSwitch: true,
        chartLayoutSwitchCssConfig: {
          iconSize: 24,
          iconUnit: 'px',
          leftIcon: 'bar-chart',
          rightIcon: 'line-chart',
          idleTextColor: 'var(--color-on-surface-muted)',
          idleBorderColor: 'var(--color-border-color)',
          idleBackgroundColor: 'var(--color-on-primary)',
          activeTextColor: 'var(--color-on-primary)',
          activeBorderColor: 'var(--color-primary)',
          activeBackgroundColor: 'var(--color-primary)',
          borderRadiusBottomRight: 6,
          borderRadiusBottomLeft: 6,
          borderRadiusTopRight: 6,
          borderRadiusTopLeft: 6,
          borderRadiusUnit: 'px',
          borderWidthTop: 1,
          borderWidthBottom: 1,
          borderWidthLeft: 1,
          borderWidthRight: 1,
          borderWidthUnit: 'px',
          borderClass: BorderRadiusClass.NONE,
          borderStyle: BorderStyles.SOLID,
        },
        chartCssConfig: {
          marginTop: 0,
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
          marginUnit: 'px',
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: 0,
          paddingRight: 0,
          paddingUnit: 'px',
        },
        chartGridColor: 'var(--color-border-color)',
        chartLabelTextColor: 'var(--color-on-surface-muted)',
        chartDataColor: 'var(--color-hover)',
        legendPosition: ChartLegendPosition.BOTTOM,
        legendInput: '',
      },
    },
    isRemovable: true,
  },
  widgetIconPath: 'chart',
} as WorkflowWidgetConfig;

export const documentListingWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: new UUID().toString(),
  name: WidgetNames.DOCUMENT_LISTING_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.DOCUMENT_LISTING_WIDGET,
    widgetConfigDisplayName: WidgetTitles.DOCUMENT_LISTING_WIDGET,
    attributes: {
      elementsConfig: [],
      css_config: {
        ...Constants.defaultBaseCssConfig,
        borderWidthTop: 1,
        borderWidthBottom: 1,
        borderWidthLeft: 1,
        borderWidthRight: 1,
        borderWidthUnit: 'px',
        borderRadiusTopLeft: 6,
        borderRadiusBottomLeft: 6,
        borderRadiusTopRight: 6,
        borderRadiusBottomRight: 6,
        borderStyle: BorderStyles.SOLID,
        borderClass: BorderRadiusKeys.NONE,
      },
      widgetProperties: {
        textContent: '',
        documentContext: DocumentActionType.CURRENT_ACCOUNTS,
        documentSubCategory: DocumentActionCategoryType.ACCOUNT_STATEMENTS,
        fetchingTemplate: 'Fetching',
        noDocumentTemplate: 'No Records',
        documentListItemTemplate: `
          <div class="p-4 main-bg-color rad-1 w-full h-full border mb-3"
               style="border-color: var(--color-border-color);">
            <div class="is-space-between">
              <div class="is-horizontal-align">
                {{{ svgIcon 'book' }}}
                <span class="pl-1 pr-1 text-capitalize">
                  {{document.name}}
                </span>
              </div>
              <span class="d-flex">
                {{{ svgIcon 'arrow-right' '32px' '32px' }}}
              </span>
            </div>
          </div>
        `,

        horizontalLayoutTemplate: `
          <div class="p-4 main-bg-color rad-1 w-full h-full border is-horizontal-align"
               style="border-color: var(--color-border-color);">
            <div style="display: inline-grid;">
              <div class="is-horizontal-align text-capitalize vertical-center">
                {{{ svgIcon 'book' }}}
              </div>
              <div class="mt-1 truncate">
                {{document.name}}
              </div>
            </div>
          </div>
        `,

        orientation: Orientation.VERTICAL,
        carouselItemWidth: 50,
        carouselItemHeight: 120,
        carouselItemHeightUnit: 'px',
        carouselItemWidthUnit: '%',
        listSize: '10',
        pagingEnabled: true,
        appBuilderPreviewOption: 'default',
      },
    },
    isRemovable: true,
  },
  widgetIconPath: 'listing',
} as WorkflowWidgetConfig;

export const productsListingWidgetConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: new UUID().toString(),
  name: WidgetNames.PRODUCTS_LISTING_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.PRODUCTS_LISTING_WIDGET,
    widgetConfigDisplayName: WidgetTitles.PRODUCTS_LISTING_WIDGET,
    attributes: {
      elementsConfig: [],
      css_config: {
        ...Constants.defaultBaseCssConfig,
        borderWidthTop: 1,
        borderWidthBottom: 1,
        borderWidthLeft: 1,
        borderWidthRight: 1,
        borderWidthUnit: 'px',
        borderRadiusTopLeft: 6,
        borderRadiusBottomLeft: 6,
        borderRadiusTopRight: 6,
        borderRadiusBottomRight: 6,
        borderStyle: BorderStyles.SOLID,
        borderClass: BorderRadiusKeys.NONE,
      },
      noRecordsTemplate: 'No products found',
      formId: '',
      inputId: '',
      customStyles: `.product-list-item { background-color: var(--color-surface); border: 1px solid var(--color-border-color) } \n .selected-item { border-color: var(--color-active) }`,
      widgetProperties: {
        textContent: `
          <div class="body1 border-1 p-2 rad-1">
            {{productName}} - {{accountType}}
          </div>
        `,
        loadingTextContent: 'Loading products..',
        pageSize: '10',
        pagingEnabled: true,
        appBuilderPreviewOption: 'default',
        productFilterConfig: {
          journeyType: ProductFilterJourneyType.NEW_SAVINGS_PRODUCT,
          customerType: ProductFilterCustomerType.RETAIL,
          accountType: ProductFilterAccountType.ALL,
        },
      },
    },
    isRemovable: true,
  },
  widgetIconPath: 'listing',
};

export const tooltipWidgetConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: new UUID().toString(),
  name: WidgetNames.TOOLTIP_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.TOOLTIP_WIDGET,
    widgetConfigDisplayName: WidgetTitles.TOOLTIP_WIDGET,
    attributes: {
      elementsConfig: [],
      css_config: {
        iconColor: 'var(--color-on-surface)',
        iconSize: '24',
        iconUnit: 'px',
      },
      widgetProperties: {
        icon: 'info',
        textContent: `
          <div class="body1 border-1 p-2 rad-1">
            This is a tooltip
          </div>
        `,
      },
    },
    isRemovable: true,
  },
  widgetIconPath: 'tooltip',
};

export const airwallexKycWidgetConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: new UUID().toString(),
  name: WidgetNames.AIRWALLEX_KYC_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.AIRWALLEX_KYC_WIDGET,
    widgetConfigDisplayName: WidgetTitles.AIRWALLEX_KYC_WIDGET,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.AIRWALLEX_KYC_WIDGET,
      css_config: {
        ...Constants.defaultBaseCssConfig,
      },
      widgetProperties: {
        textContent: 'Complete your verification to activate your account.',
      },
    },
    isRemovable: true,
  },
  // The embedded KYC form is tall; a default-height tile would clip it.
  gridPosition: {
    x: 0,
    y: 0,
    cols: 12,
    rows: 20,
    minItemCols: 6,
    minItemRows: 8,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'integration',
};

export const authenticationFlowWidgetConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: new UUID().toString(),
  name: WidgetNames.AUTHENTICATION_FLOW_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.AUTHENTICATION_FLOW_WIDGET,
    widgetConfigDisplayName: WidgetTitles.AUTHENTICATION_FLOW_WIDGET,
    attributes: {
      elementsConfig: [],
      css_config: {
        ...Constants.defaultBaseCssConfig,
      },
      widgetProperties: {
        textContent: '',
        // Seeds the flow with `login_hint` from the parent form when the page collects
        // an identifier before the widget is reached.
        loginHintFromForm: false,
      },
    },
    isRemovable: true,
  },
  gridPosition: {
    x: 0,
    y: 0,
    cols: 12,
    rows: 20,
    minItemCols: 6,
    minItemRows: 8,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'generic-widget',
};
